import React, { useState } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { RiEyeLine, RiEyeOffLine, RiUser3Line, RiLockLine } from 'react-icons/ri';
import '../styles/Signin.css';
import { useApiCall } from '../hooks/useApiCall';
import { useAccount } from '../contexts/AccountContext';
import { useCart } from '../contexts/CartContext';

const Signin = () => {
  const apiCall = useApiCall();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { setAccount } = useAccount();
  const { syncGuestToAccount, refreshCounts } = useCart();
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  // Check for reset success message
  React.useEffect(() => {
    if (searchParams.get('reset') === 'success') {
      setSuccessMessage('Your password has been reset successfully. Please sign in with your new password.');
    }
  }, [searchParams]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    setIsLoading(true);
    

    apiCall('auth/signin', 'POST', {
        email: formData.email.trim(),
        password: formData.password.trim()
      }, null, null).then(async (response) => {


        if(response.success){

          setAccount(response.data?.account);
          await syncGuestToAccount(response.data?.account);
          refreshCounts();
          
          // Handle redirect after successful sign in
          const ref = searchParams.get('ref');
          if(ref) {
            navigate(`/${ref}`);
          } else {
            navigate('/');
          }
        }else{

          setErrors({ general: response?.response });
        }

        setIsLoading(false);
    });

     // Mock successful sign in - use AccountContext instead of direct localStorage
  };

  return (
    <div className="signin-container">
      <div className="signin-content">
        <div className="signin-form-wrapper">
          <div className="signin-header">
            <h1>Welcome Back</h1>
            <p>Sign in to your Building Depot Suriname account</p>
          </div>

          <form onSubmit={handleSubmit} className="signin-form">
            {successMessage && (
              <div className="success-message general-success">
                {successMessage}
              </div>
            )}
            
            {errors.general && (
              <div className="error-message general-error">
                {errors.general}
              </div>
            )}

            <div className="form-group">
              <label htmlFor="email">Email Address</label>
              <div className="input-wrapper">
                <RiUser3Line className="input-icon" />
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="Enter your email"
                  className={errors.email ? 'error' : ''}
                />
              </div>
              {errors.email && <span className="error-message">{errors.email}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="password">Password</label>
              <div className="input-wrapper">
                <RiLockLine className="input-icon" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  id="password"
                  name="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  placeholder="Enter your password"
                  className={errors.password ? 'error' : ''}
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <RiEyeOffLine /> : <RiEyeLine />}
                </button>
              </div>
              {errors.password && <span className="error-message">{errors.password}</span>}
            </div>

            <div className="form-options">
              <Link to="/forgot-password" className="forgot-password">
                Forgot password?
              </Link>
            </div>

            <button 
              type="submit" 
              className="signin-btn"
              disabled={isLoading}
            >
              {isLoading ? 'Signing in...' : 'Sign In'}
            </button>
          </form>

          <div className="signin-footer">
            <p>
              Don't have an account? 
              <Link to="/sign-up" className="signup-link"> Create account</Link>
            </p>
          </div>
        </div>

        <div className="signin-image">
          <div className="image-content">
            <h2>Building Depot Suriname</h2>
            <p>Lights & Living</p>
            <div className="image-placeholder">
              <img 
                src="https://images.unsplash.com/photo-1586023492125-27b2c045efd7?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80" 
                alt="Modern lighting display"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Signin;